import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, XCircle, Clock, Activity, 
  Twitter, Facebook, Camera, Phone, 
  AlertTriangle, ChevronRight, Globe,
  ThumbsUp, ThumbsDown, ChevronDown,
  Send, Minimize2  
} from 'lucide-react';

interface Source {
  id: string;
  type: 'twitter' | 'facebook' | 'camera' | 'call' | 'crowd' | 'image';
  content: string;
  timestamp: string;
  location: string;
  verified: boolean;
  confidence?: number;
}

interface Incident {
  id: string;
  title: string;
  location: string;
  timestamp: string;
  sources: Source[];
  confidence: number;
  status: 'pending' | 'verified' | 'rejected';
  category: 'flood' | 'fire' | 'collapse' | 'medical' | 'other';
}

const VerificationEngine: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([
    {
      id: '1',
      title: 'Flooding in Zone A - Critical',
      location: 'Zone A, Sector 7',
      timestamp: '3:47 PM',
      confidence: 94,
      status: 'verified',
      category: 'flood',
      sources: [
        { id: 's1', type: 'camera', content: 'Public Camera #A-234: Water level rising rapidly', timestamp: '3:47 PM', location: 'Zone A', verified: true, confidence: 98 },
        { id: 's2', type: 'twitter', content: '@rescue_alert: "Water rising fast in Zone A! 3rd floor submerged"', timestamp: '3:48 PM', location: 'Zone A', verified: true, confidence: 92 },
        { id: 's3', type: 'call', content: 'Emergency call #4472: "Trapped on roof with 4 people"', timestamp: '3:49 PM', location: 'Zone A', verified: true, confidence: 95 },
        { id: 's4', type: 'image', content: 'AI Image Analysis: 12 people detected on rooftops', timestamp: '3:52 PM', location: 'Zone A', verified: true, confidence: 94 },
      ]
    },
    {
      id: '2',
      title: 'Fire in Zone B Market',
      location: 'Zone B, Market Area',
      timestamp: '4:12 PM',
      confidence: 67,
      status: 'pending',
      category: 'fire',
      sources: [
        { id: 's5', type: 'facebook', content: 'Facebook post: "Fire near market, smoke visible"', timestamp: '4:12 PM', location: 'Zone B', verified: false, confidence: 45 },
        { id: 's6', type: 'camera', content: 'Camera feed #B-123: Smoke detected in distance', timestamp: '4:15 PM', location: 'Zone B', verified: false, confidence: 62 },
        { id: 's7', type: 'crowd', content: 'Crowd report: 3 confirmations from browser extension', timestamp: '4:18 PM', location: 'Zone B', verified: false, confidence: 58 },
      ]
    },
    {
      id: '3',
      title: 'Building Collapse - Zone C',
      location: 'Zone C, Residential Area',
      timestamp: '5:30 PM',
      confidence: 82,
      status: 'verified',
      category: 'collapse',
      sources: [
        { id: 's8', type: 'camera', content: 'Public Camera #C-456: Building partially collapsed', timestamp: '5:30 PM', location: 'Zone C', verified: true, confidence: 96 },
        { id: 's9', type: 'call', content: 'Multiple emergency calls received', timestamp: '5:32 PM', location: 'Zone C', verified: true, confidence: 88 },
        { id: 's10', type: 'image', content: 'AI Image Analysis: 7 people possibly trapped', timestamp: '5:35 PM', location: 'Zone C', verified: true, confidence: 85 },
      ]
    }
  ]);

  const [expandedIncident, setExpandedIncident] = useState<string | null>(null);

  const getSourceIcon = (type: string) => {
    switch(type) {
      case 'twitter': return <Twitter size={16} className="text-cyan-400" />;
      case 'facebook': return <Facebook size={16} className="text-blue-400" />;
      case 'camera': return <Camera size={16} className="text-green-400" />;
      case 'call': return <Phone size={16} className="text-yellow-400" />;
      case 'crowd': return <Globe size={16} className="text-purple-400" />;
      case 'image': return <Activity size={16} className="text-pink-400" />;
      default: return <Activity size={16} className="text-gray-400" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch(category) {
      case 'flood': return 'text-blue-500 border-blue-500';
      case 'fire': return 'text-orange-500 border-orange-500';
      case 'collapse': return 'text-red-500 border-red-500';
      case 'medical': return 'text-green-500 border-green-500';
      default: return 'text-gray-500 border-gray-500';
    }
  };

  const verifyIncident = (id: string) => {
    setIncidents(incidents.map(inc => 
      inc.id === id ? { ...inc, status: 'verified', confidence: 94 } : inc
    ));
  };

  const rejectIncident = (id: string) => {
    setIncidents(incidents.map(inc => 
      inc.id === id ? { ...inc, status: 'rejected', confidence: 0 } : inc
    ));
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Activity className="text-purple-500 animate-pulse" />
            10-MIN VERIFICATION ENGINE
          </h2>
          <p className="text-xs text-gray-400 mt-1">Cross-referencing multiple sources for accuracy</p>
        </div>
        <div className="flex gap-2">
          <span className="text-xs bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full border border-yellow-500/30">
            Pending: {incidents.filter(i => i.status === 'pending').length}
          </span>
          <span className="text-xs bg-green-500/20 text-green-500 px-3 py-1 rounded-full border border-green-500/30">
            Verified: {incidents.filter(i => i.status === 'verified').length}
          </span>
        </div>
      </div>

      {/* Timeline Header */}
      <div className="flex items-center gap-2 mb-4 text-xs text-gray-400">
        <Clock size={14} />
        <span>Real-time verification timeline (last 60 minutes)</span>
      </div>

      <div className="space-y-4">
        {incidents.map((incident) => (
          <motion.div
            key={incident.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border rounded-xl overflow-hidden transition-all ${
              incident.status === 'verified' ? 'border-green-500/30 bg-green-500/5' :
              incident.status === 'rejected' ? 'border-red-500/30 bg-red-500/5' :
              'border-yellow-500/30 bg-yellow-500/5'
            }`}
          >
            {/* Incident Header */}
            <div 
              className="p-4 cursor-pointer hover:bg-white/5"
              onClick={() => setExpandedIncident(expandedIncident === incident.id ? null : incident.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full border ${getCategoryColor(incident.category)}`}>
                      {incident.category.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-400">{incident.location}</span>
                  </div>
                  <h3 className="font-bold text-lg">{incident.title}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-gray-400">{incident.timestamp}</span>
                    <div className="flex items-center gap-1">
                      <Globe size={14} className="text-gray-400" />
                      <span className="text-gray-400">{incident.sources.length} sources</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Confidence Meter */}
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${
                      incident.confidence > 80 ? 'text-green-500' :
                      incident.confidence > 60 ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                      {incident.confidence}%
                    </div>
                    <div className="text-xs text-gray-400">confidence</div>
                  </div>
                  
                  {/* Status Icon */}
                  {incident.status === 'verified' && <CheckCircle className="text-green-500" size={24} />}
                  {incident.status === 'rejected' && <XCircle className="text-red-500" size={24} />}
                  {incident.status === 'pending' && <Clock className="text-yellow-500 animate-spin" size={24} />}
                  
                  {/* Expand/Collapse */}
                  <button className="p-1 hover:bg-white/10 rounded">
                    <Minimize2 size={16} className={`transform transition-transform ${
                      expandedIncident === incident.id ? 'rotate-180' : ''
                    }`} />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3 w-full bg-gray-700 h-1 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${incident.confidence}%` }}
                  className={`h-1 ${
                    incident.confidence > 80 ? 'bg-green-500' :
                    incident.confidence > 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                />
              </div>
            </div>

            {/* Expanded Sources Timeline */}
            <AnimatePresence>
              {expandedIncident === incident.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-gray-700/50 p-4 bg-black/30"
                >
                  <p className="text-sm font-bold mb-3 flex items-center gap-2">
                    <Clock size={14} className="text-cyan-500" />
                    Verification Timeline
                  </p>
                  
                  <div className="relative pl-6 mb-4">
                    <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-cyan-500 to-purple-500" />
                    
                    {incident.sources.map((source, idx) => (
                      <motion.div
                        key={source.id}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="relative mb-4 flex items-start gap-3"
                      >
                        <div className={`absolute -left-4 top-1 w-2 h-2 rounded-full ${
                          source.verified ? 'bg-green-500' : 'bg-yellow-500'
                        } animate-pulse`} />
                        
                        {getSourceIcon(source.type)}
                        
                        <div className="flex-1">
                          <p className="text-sm">{source.content}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">{source.timestamp}</span>
                            <span className="text-xs text-gray-500">{source.location}</span>
                            {source.confidence && (
                              <span className={`text-xs ${
                                source.confidence > 80 ? 'text-green-500' :
                                source.confidence > 60 ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                                {source.confidence}% confidence
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {source.verified ? (
                          <CheckCircle size={14} className="text-green-500" />
                        ) : (
                          <Clock size={14} className="text-yellow-500" />
                        )}
                      </motion.div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  {incident.status === 'pending' && (
                    <div className="flex gap-3 mt-4">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => verifyIncident(incident.id)}
                        className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                      >
                        <ThumbsUp size={16} />
                        VERIFY INCIDENT
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => rejectIncident(incident.id)}
                        className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                      >
                        <ThumbsDown size={16} />
                        REJECT
                      </motion.button>
                    </div>
                  )}

                  {incident.status === 'verified' && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 mt-4"
                    >
                      <Send size={16} />
                      DISPATCH RESCUE TEAMS
                      <ChevronRight size={16} />
                    </motion.button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Stats Footer */}
      <div className="mt-6 grid grid-cols-4 gap-4 text-center">
        <div className="p-3 bg-black/30 rounded-lg border border-cyan-500/30">
          <div className="text-2xl font-bold text-cyan-500">10</div>
          <div className="text-xs text-gray-400">Avg. Verify Time</div>
        </div>
        <div className="p-3 bg-black/30 rounded-lg border border-cyan-500/30">
          <div className="text-2xl font-bold text-green-500">94%</div>
          <div className="text-xs text-gray-400">Accuracy</div>
        </div>
        <div className="p-3 bg-black/30 rounded-lg border border-cyan-500/30">
          <div className="text-2xl font-bold text-yellow-500">847</div>
          <div className="text-xs text-gray-400">Sources</div>
        </div>
        <div className="p-3 bg-black/30 rounded-lg border border-cyan-500/30">
          <div className="text-2xl font-bold text-purple-500">24/7</div>
          <div className="text-xs text-gray-400">Monitoring</div>
        </div>
      </div>
    </div>
  );
};

export default VerificationEngine;